import { ModuleId, Question, PlacementResult } from '../types'
import {
  ALL_QUESTIONS_CORE,
  getQuestionWithExplanation,
  shuffleQuestionWithExplanations,
} from '../data/questionLoader'
import { getLevelsByGroup, LevelConfig } from '../config/levels'
import { GroupId } from '../config/systemConfig'

/**
 * Placement Engine v1.0
 *
 * Generates a short diagnostic test (5 questions) to determine
 * the appropriate starting level for a student.
 *
 * Strategy:
 * - Sample questions from the MIDDLE level of the group (M6, M7, M8)
 * - If not enough questions at middle, also sample from B+ level
 * - Score determines starting level:
 *   - 4-5 correct → Start at M level (skip B and B+)
 *   - 2-3 correct → Start at B+ level (skip B)
 *   - 0-1 correct → Start at B level (normal start)
 */

const PLACEMENT_CONFIG = {
  /** Number of questions in the placement test */
  QUESTION_COUNT: 5,
  /** Score threshold to skip to middle level (M) */
  SKIP_TO_MIDDLE_THRESHOLD: 4,
  /** Score threshold to skip to B+ level */
  SKIP_TO_BPLUS_THRESHOLD: 2,
}

/**
 * Get the level IDs for placement test targeting.
 * Returns { beginLevel, beginPlusLevel, middleLevel } for a group.
 */
function getPlacementLevels(group: GroupId): {
  beginLevel: LevelConfig
  beginPlusLevel: LevelConfig
  middleLevel: LevelConfig
} | null {
  const levels = getLevelsByGroup(group)
  if (levels.length < 3) return null

  // Levels are ordered: B, B+, M, M+, E, E+
  return {
    beginLevel: levels[0], // B6 / B7 / B8
    beginPlusLevel: levels[1], // B6+ / B7+ / B8+
    middleLevel: levels[2], // M6 / M7 / M8
  }
}

/**
 * Generate placement test questions for a module + group.
 * Samples from the middle level (M) to test if student can handle it.
 * Falls back to B+ if not enough M-level questions.
 */
export function generatePlacementQuestions(
  moduleId: ModuleId,
  group: GroupId,
): Question[] {
  const placementLevels = getPlacementLevels(group)
  if (!placementLevels) return []

  const { middleLevel, beginPlusLevel } = placementLevels

  // Get questions from middle level
  let candidates = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId && q.difficulty === middleLevel.id,
  )

  // If not enough, also include B+ level questions
  if (candidates.length < PLACEMENT_CONFIG.QUESTION_COUNT) {
    const bPlusCandidates = ALL_QUESTIONS_CORE.filter(
      (q) => q.module === moduleId && q.difficulty === beginPlusLevel.id,
    )
    candidates = [...candidates, ...bPlusCandidates]
  }

  if (candidates.length === 0) return []

  // Diversify by subSkill: pick at most 1-2 per subSkill
  const bySubSkill = new Map<string, typeof candidates>()
  candidates.forEach((q) => {
    const key = q.subSkill || 'general'
    if (!bySubSkill.has(key)) bySubSkill.set(key, [])
    bySubSkill.get(key)!.push(q)
  })

  // Round-robin pick from each subSkill
  const selected: typeof candidates = []
  const subSkillKeys = Array.from(bySubSkill.keys())
  let round = 0

  while (selected.length < PLACEMENT_CONFIG.QUESTION_COUNT && round < 3) {
    for (const key of subSkillKeys) {
      if (selected.length >= PLACEMENT_CONFIG.QUESTION_COUNT) break
      const pool = bySubSkill.get(key)!
      if (pool.length > round) {
        // Shuffle within pool for randomness
        const shuffled = [...pool].sort(() => Math.random() - 0.5)
        if (shuffled[round] && !selected.includes(shuffled[round])) {
          selected.push(shuffled[round])
        }
      }
    }
    round++
  }

  // Fill remaining if needed
  if (selected.length < PLACEMENT_CONFIG.QUESTION_COUNT) {
    const remaining = candidates.filter((q) => !selected.includes(q))
    const shuffled = remaining.sort(() => Math.random() - 0.5)
    selected.push(
      ...shuffled.slice(0, PLACEMENT_CONFIG.QUESTION_COUNT - selected.length),
    )
  }

  // Convert to full questions with explanations
  const questions = selected
    .slice(0, PLACEMENT_CONFIG.QUESTION_COUNT)
    .map((q) => getQuestionWithExplanation(q.id))
    .filter((q): q is Question => q !== null)
    // FIX: Shuffle options to prevent answer position bias
    // Previously, correctAnswer indices remained as authored in JSON,
    // creating systematic bias (e.g., 53% of correct answers at position 0)
    .map((q) => shuffleQuestionWithExplanations(q))

  // Shuffle final order
  return questions.sort(() => Math.random() - 0.5)
}

/**
 * Evaluate placement test results and determine starting level.
 */
export function evaluatePlacement(
  moduleId: ModuleId,
  group: GroupId,
  correctCount: number,
  totalQuestions: number,
): PlacementResult {
  const placementLevels = getPlacementLevels(group)
  const levels = getLevelsByGroup(group)

  let startingLevelId: number

  if (!placementLevels || levels.length < 3) {
    // Fallback: start at first level
    startingLevelId = levels[0]?.id || 101
  } else if (correctCount >= PLACEMENT_CONFIG.SKIP_TO_MIDDLE_THRESHOLD) {
    // Strong student: start at middle level (M6/M7/M8)
    startingLevelId = placementLevels.middleLevel.id
    console.log(
      `🎯 Placement: ${moduleId} → ${placementLevels.middleLevel.label} (${correctCount}/${totalQuestions} correct)`,
    )
  } else if (correctCount >= PLACEMENT_CONFIG.SKIP_TO_BPLUS_THRESHOLD) {
    // Moderate student: start at B+ level
    startingLevelId = placementLevels.beginPlusLevel.id
    console.log(
      `🎯 Placement: ${moduleId} → ${placementLevels.beginPlusLevel.label} (${correctCount}/${totalQuestions} correct)`,
    )
  } else {
    // Needs foundation: start at B level
    startingLevelId = placementLevels.beginLevel.id
    console.log(
      `🎯 Placement: ${moduleId} → ${placementLevels.beginLevel.label} (${correctCount}/${totalQuestions} correct)`,
    )
  }

  return {
    moduleId,
    group,
    startingLevelId,
    score: correctCount,
    totalQuestions,
    completedAt: Date.now(),
  }
}

/**
 * Get the placement key for storage lookup.
 */
export function getPlacementKey(moduleId: ModuleId, group: GroupId): string {
  return `${moduleId}_${group}`
}

/**
 * Check if a placement test is available for a module+group.
 * Returns true if there are enough questions to generate a test.
 */
export function isPlacementAvailable(
  moduleId: ModuleId,
  group: GroupId,
): boolean {
  const questions = generatePlacementQuestions(moduleId, group)
  return questions.length >= 3 // Need at least 3 questions for a meaningful test
}

/**
 * Get a human-readable description of what the placement test result means.
 */
export function getPlacementDescription(result: PlacementResult): {
  title: string
  description: string
  emoji: string
} {
  const levels = getLevelsByGroup(result.group as GroupId)
  const startLevel = levels.find((l) => l.id === result.startingLevelId)
  const label = startLevel?.label || '?'

  if (result.score >= PLACEMENT_CONFIG.SKIP_TO_MIDDLE_THRESHOLD) {
    return {
      title: `Je start op ${label}!`,
      description: `Wow, ${result.score} van de ${result.totalQuestions} goed! Je kent de basis al goed. We beginnen op een hoger niveau.`,
      emoji: '🚀',
    }
  } else if (result.score >= PLACEMENT_CONFIG.SKIP_TO_BPLUS_THRESHOLD) {
    return {
      title: `Je start op ${label}`,
      description: `${result.score} van de ${result.totalQuestions} goed. Je hebt al wat kennis, we slaan het makkelijkste niveau over.`,
      emoji: '👍',
    }
  } else {
    return {
      title: `Je start op ${label}`,
      description: `We beginnen bij de basis zodat je stap voor stap sterker wordt. Geen zorgen, je gaat snel vooruit!`,
      emoji: '🌱',
    }
  }
}
